import React, { useMemo, useState } from 'react';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import { fmt } from '../utils';

const KNOWN_STABLES = [
  'USDC', 'USDT', 'DAI', 'FRAX', 'LUSD', 'sUSD', 'GHO', 'crvUSD',
  'USDC.e', 'USDbC', 'PYUSD', 'USDS', 'TUSD', 'BUSD', 'GUSD', 'RAI',
  'DOLA', 'MIM', 'EUSD', 'USD+', 'USDD', 'USDP', 'alUSD', 'eUSD',
  'FDUSD',
];

const ETH_TOKENS = ['ETH', 'WETH', 'stETH', 'wstETH', 'rETH', 'cbETH', 'OETH', 'weETH', 'pufETH', 'frxETH', 'sfrxETH'];

function classifyPool(pool) {
  const upper = (pool.tokens || []).map(t => t.toUpperCase());
  const stableCount = upper.filter(t => KNOWN_STABLES.some(s => t.includes(s.toUpperCase()))).length;
  if (stableCount >= upper.length && upper.length > 0) return 'stablecoin';
  const hasEth = upper.some(t => ETH_TOKENS.some(e => t.includes(e.toUpperCase())));
  if (hasEth) return 'eth';
  return 'exotic';
}

function getGreenIntensity(apr, maxApr, dark) {
  if (maxApr === 0) return dark ? 'rgba(46,160,67,0.05)' : 'rgba(13,122,78,0.05)';
  const ratio = Math.min(apr / maxApr, 1);
  const clamped = Math.max(0.05, ratio);
  if (dark) {
    return `rgba(46,160,67,${(clamped * 0.6).toFixed(2)})`;
  }
  return `rgba(13,122,78,${(clamped * 0.45).toFixed(2)})`;
}

export default function HeatmapView({ pools, onSelectPool }) {
  const { dark, colors } = useTheme();
  const isMobile = useIsMobile();
  const [hoveredCell, setHoveredCell] = useState(null);

  // Derive protocols and chains dynamically from pool data, sorted by pool count descending
  const protocols = useMemo(() => {
    if (!pools || pools.length === 0) return [];
    const counts = {};
    pools.forEach(p => { counts[p.protocol] = (counts[p.protocol] || 0) + 1; });
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  }, [pools]);

  const chains = useMemo(() => {
    if (!pools || pools.length === 0) return [];
    const counts = {};
    pools.forEach(p => { counts[p.chain] = (counts[p.chain] || 0) + 1; });
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  }, [pools]);

  const { matrix, maxApr } = useMemo(() => {
    const m = {};
    let max = 0;
    protocols.forEach(proto => {
      m[proto] = {};
      chains.forEach(chain => {
        const matching = pools.filter(p => p.protocol === proto && p.chain === chain);
        const avgApr = matching.length > 0
          ? matching.reduce((s, p) => s + (p.apr || 0), 0) / matching.length
          : 0;
        const totalTvl = matching.reduce((s, p) => s + p.tvl, 0);
        const best = matching.length > 0
          ? matching.reduce((b, p) => p.apr > b.apr ? p : b, matching[0])
          : null;
        m[proto][chain] = { avgApr, totalTvl, count: matching.length, best, pools: matching };
        if (avgApr > max) max = avgApr;
      });
    });
    return { matrix: m, maxApr: max };
  }, [pools, protocols, chains]);

  const categories = useMemo(() => {
    const cats = { stablecoin: [], eth: [], exotic: [] };
    pools.forEach(p => {
      const cat = classifyPool(p);
      cats[cat].push(p);
    });
    Object.keys(cats).forEach(k => {
      cats[k].sort((a, b) => b.apr - a.apr);
      cats[k] = cats[k].slice(0, 5);
    });
    return cats;
  }, [pools]);

  const categoryMeta = {
    stablecoin: { title: 'Stablecoin Pools', icon: '\u25C6' },
    eth:        { title: 'ETH-Paired Pools', icon: '\u039E' },
    exotic:     { title: 'Exotic Pools',     icon: '\u2726' },
  };

  return (
    <div style={{ padding: isMobile ? '16px' : '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 26,
          color: colors.textPrimary,
          margin: 0,
          transition: 'color 0.3s ease',
        }}>
          Yield Heatmap
        </h2>
        <p style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
          margin: '6px 0 0 0',
        }}>
          Average APR across protocols and chains. Darker green = higher yield.
        </p>
      </div>

      {/* Heatmap Matrix */}
      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: isMobile ? 16 : 24,
        marginBottom: 32,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 4,
          }}>
            <thead>
              <tr>
                <th style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  color: colors.labelMuted,
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontWeight: 500,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  width: 120,
                }}>
                  Protocol
                </th>
                {chains.map(chain => (
                  <th key={chain} style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10,
                    color: colors.labelMuted,
                    textAlign: 'center',
                    padding: '8px 12px',
                    fontWeight: 500,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <ChainIcon chain={chain} size={14} />
                      {chain}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {protocols.map(proto => (
                <tr key={proto}>
                  <td style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    padding: '8px 12px',
                    transition: 'color 0.3s ease',
                  }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <ProtocolIcon protocol={proto} size={14} />
                      {proto}
                    </span>
                  </td>
                  {chains.map(chain => {
                    const cell = matrix[proto][chain];
                    const cellKey = `${proto}-${chain}`;
                    const isHovered = hoveredCell === cellKey;
                    return (
                      <td
                        key={chain}
                        onMouseEnter={() => setHoveredCell(cellKey)}
                        onMouseLeave={() => setHoveredCell(null)}
                        style={{
                          position: 'relative',
                          textAlign: 'center',
                          padding: 0,
                        }}
                      >
                        <div style={{
                          background: cell.count > 0
                            ? getGreenIntensity(cell.avgApr, maxApr, dark)
                            : dark ? 'rgba(110,118,129,0.06)' : 'rgba(0,0,0,0.02)',
                          borderRadius: 6,
                          padding: '16px 12px',
                          cursor: cell.count > 0 ? 'pointer' : 'default',
                          border: isHovered && cell.count > 0
                            ? `1px solid ${colors.green}`
                            : `1px solid transparent`,
                          transition: 'border-color 0.15s ease',
                          minWidth: 90,
                        }}>
                          {cell.count > 0 ? (
                            <>
                              <div style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 16,
                                fontWeight: 700,
                                color: colors.textPrimary,
                                lineHeight: 1.2,
                              }}>
                                {cell.avgApr.toFixed(1)}%
                              </div>
                              <div style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 9,
                                color: colors.labelMuted,
                                marginTop: 3,
                              }}>
                                {cell.count} pool{cell.count !== 1 ? 's' : ''}
                              </div>
                            </>
                          ) : (
                            <div style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 12,
                              color: colors.labelLight,
                            }}>
                              --
                            </div>
                          )}
                        </div>

                        {/* Tooltip */}
                        {isHovered && cell.count > 0 && (
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginBottom: 8,
                            background: dark ? '#2D333B' : '#1B1F24',
                            color: '#E6EDF3',
                            borderRadius: 8,
                            padding: '10px 14px',
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 11,
                            whiteSpace: 'nowrap',
                            zIndex: 20,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                            lineHeight: 1.7,
                            textAlign: 'left',
                            pointerEvents: 'none',
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>
                              {proto} on {chain}
                            </div>
                            <div>Pools: <span style={{ color: '#7EE787' }}>{cell.count}</span></div>
                            <div>Avg APR: <span style={{ color: '#7EE787' }}>{cell.avgApr.toFixed(2)}%</span></div>
                            <div>Total TVL: <span style={{ color: '#7EE787' }}>{fmt(cell.totalTvl)}</span></div>
                            {cell.best && (
                              <div>Best: <span style={{ color: '#7EE787' }}>{cell.best.name} ({(cell.best.apr || 0).toFixed(2)}%)</span></div>
                            )}
                            <div style={{
                              position: 'absolute',
                              bottom: -5,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 0,
                              height: 0,
                              borderLeft: '5px solid transparent',
                              borderRight: '5px solid transparent',
                              borderTop: `5px solid ${dark ? '#2D333B' : '#1B1F24'}`,
                            }} />
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 16,
          paddingTop: 14,
          borderTop: `1px solid ${colors.border}`,
        }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelMuted,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}>
            Intensity
          </span>
          {[0.05, 0.15, 0.25, 0.35, 0.45].map((v, i) => (
            <div key={i} style={{
              width: 24,
              height: 14,
              borderRadius: 3,
              background: dark
                ? `rgba(46,160,67,${(v * 0.6).toFixed(2)})`
                : `rgba(13,122,78,${(v * 0.45).toFixed(2)})`,
            }} />
          ))}
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelMuted,
          }}>
            Low
          </span>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelMuted,
            marginLeft: 'auto',
          }}>
            High
          </span>
        </div>
      </div>

      {/* Top Yields by Category */}
      <div style={{ marginBottom: 12 }}>
        <h3 style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 20,
          color: colors.textPrimary,
          margin: '0 0 16px 0',
          transition: 'color 0.3s ease',
        }}>
          Top Yields by Category
        </h3>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: 16,
      }}>
        {['stablecoin', 'eth', 'exotic'].map(catKey => {
          const meta = categoryMeta[catKey];
          const topPools = categories[catKey];
          return (
            <div key={catKey} style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              padding: 18,
              transition: 'background-color 0.3s ease, border-color 0.3s ease',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 14,
              }}>
                <span style={{
                  fontSize: 14,
                  color: colors.green,
                  lineHeight: 1,
                }}>
                  {meta.icon}
                </span>
                <span style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 15,
                  color: colors.textPrimary,
                  transition: 'color 0.3s ease',
                }}>
                  {meta.title}
                </span>
              </div>

              {topPools.length === 0 ? (
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11,
                  color: colors.labelMuted,
                  padding: '16px 0',
                  textAlign: 'center',
                }}>
                  No pools found
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {topPools.map((pool, idx) => (
                    <div
                      key={pool.id}
                      onClick={() => onSelectPool && onSelectPool(pool)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 10px',
                        borderRadius: 6,
                        background: dark ? 'rgba(46,160,67,0.04)' : 'rgba(13,122,78,0.03)',
                        border: `1px solid ${colors.borderSubtle}`,
                        cursor: 'pointer',
                        transition: 'border-color 0.15s ease',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = colors.green;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = colors.borderSubtle;
                      }}
                    >
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10,
                        fontWeight: 600,
                        color: colors.labelMuted,
                        width: 14,
                        textAlign: 'center',
                      }}>
                        {idx + 1}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 11,
                          fontWeight: 600,
                          color: colors.textPrimary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          transition: 'color 0.3s ease',
                        }}>
                          {pool.name}
                        </div>
                        <div style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 9,
                          color: colors.labelMuted,
                          marginTop: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                        }}>
                          <ProtocolIcon protocol={pool.protocol} size={10} /> {pool.protocol} {'\u00B7'} <ChainIcon chain={pool.chain} size={10} /> {pool.chain}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 12,
                          fontWeight: 700,
                          color: colors.green,
                        }}>
                          {(pool.apr || 0).toFixed(2)}%
                        </div>
                        <div style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 9,
                          color: colors.labelMuted,
                          marginTop: 1,
                        }}>
                          {fmt(pool.tvl)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
